import {IReg, IRegPointer, REG} from 'lib/commons/registry';
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {Desk} from "lib/commons/desk";
import {ACTION} from "lib/commons/actions";
import {LANG} from "lib/commons/lang";
import {Button, OButtonActionsInit} from "back/commons/widgets/buttons";

/** Widget avec un cycle d'initialisation. */
export interface IEltInitable extends HTMLElement {

	/**
	 * Initialise le widget via les paramètres de la méthode et/ou les attributs du widget.
	 * Seul le 1er appel à cette méthode est pris en compte. Les appels suivants sur la même instance sont ignorés.
	 * Cette méthode est automatiquement appelée, sans paramètre, lorsque l'élément est inséré
	 * dans le document via CustomElement.connectedCallback().
	 *
	 * IMPORTANT : pour initialiser le widget avec des paramètres, l'appel doit être réalisé avant l'insertion du widget dans le document.
	 *
	 * Note : certaines méthodes du widget pourraient ne pas fonctionner correctement avant cette initialisation.
	 */
	initialize(init?: any): this

	/** Indique si le widget a été initialisé. */
	readonly initialized: boolean

	/**
	 * Création ou enrichissement d'un objet d'init à partir des attributs de l'élement destiné à
	 * alimenter par défaut initialize(init). Si l'implémentation utilise l'attribut
	 * pour mémoriser le state ou la config correspondant, la propriété n'est pas renseignée
	 * dans l'objet init retourné.
	 *
	 * @param init Objet déjà construit, éventuellement pré-renseigné, avec un reg par exemple.
	 * @return L'objet init enrichi ou instancié s'il n'était pas spécifié en paramètre.
	 */
	buildInitFromAtts(init?: any): any
}

/** Widget avec un cycle d'initialisation asynchrone. */
export interface IEltInitableAsync extends IEltInitable {

	/** Statut du widget vis à vis de son cycle d'initilaisation. */
	initStatus: 'unInited' | 'pending' | 'inited' | 'initFailed'

	/** Promesse résolue (ou rejetée) lorsque le widget est initialisé (ou en échec). */
	initializedAsync: Promise<this>
}

/** Widget gérant des états et un processus de refresh. */
export interface IEltRefreshable extends HTMLElement {

	/** Enclenche le processus de refresh. */
	refresh(): void

	/**
	 * Gèle ou dégèle l'élément et ses sous-élements internes (dans son shadowRoot).
	 * Permet de bloquer les refresh pendant un batch.
	 *
	 * try {
	 *    widget.refreshFreeze(1);
	 *    ...
	 * } finally {
	 *    widget.refreshFreeze(-1);
	 *    widget.refresh();
	 * }
	 *
	 * @see BaseElement.refreshFreezeAll(elt, delta)
	 */
	refreshFreeze(delta: number): void
}

export function isEltRefreshable(elt: any): elt is IEltRefreshable {return elt && ('refreshFreeze' in elt)}


/** Config pour skin personnalisé du widget. */
export interface OSkinableInit extends IRegPointer<any> {

	/** Registre à utiliser pour résoudre les skins. */
	reg?: IReg<any>

	/** Code extPoints du skin principal personnalisé à installer. */
	skin?: string

	/** Codes extPoints séparés par des espaces de skins de surcharge du skin principal à installer. */
	skinOver?: string
}

/**
 * Base pour tous les éléments IEltInitable et IEltStatable.
 *
 * Attributes :
 * Config attributes: skin, skin-over
 */
export class BaseElement extends HTMLElement implements CustomElement, IEltInitable, IEltRefreshable {
	/** Gèle ou dégèle toute une descendance. */
	static refreshFreezeAll(elt: IEltRefreshable, delta: number) {
		elt.refreshFreeze(delta);
		for (let ch = DOM.findFirstChild(elt, isEltRefreshable); ch; ch = DOM.findNextSibling(ch, isEltRefreshable)) {
			BaseElement.refreshFreezeAll(ch, delta);
		}
	}

	protected _refreshFreeze = 1;

	get initialized(): boolean {return this._initialize === null}

	initialize(init?: any): this {
		if (this._initialize !== null) {
			this._initialize(init == null ? this.buildInitFromAtts() : init);
			this._initialize = null;
			this._refreshFreeze--;
		} else if (init) {
			console.warn("Widget already initialized", this, init);
		}
		return this;
	}

	refresh() {
		if (this._shouldRefresh()) this._refresh();
	}

	refreshFreeze(delta: number) {
		this._refreshFreeze += delta;
	}

	connectedCallback() {
		//on saute l'appel à this.refresh() et this._shouldRefresh(): le refresh s'impose à ce stade (sauf si _refreshFreeze>0).
		//Plus optimisé pour les widgets agrégeant d'autres widgets qui dispatchent le refresh sur les
		//sous-widgets dans la méthode refresh() et pas _refresh(), ce qui est recommandé.
		this.initialize();
		if (this._refreshFreeze === 0) this._refresh();
	}

	/** Evalue si l'état et la situation du widget justifie un appel à _refresh()*/
	protected _shouldRefresh(): boolean {return this._refreshFreeze === 0 && this.isConnected}

	/** Execute un refresh du widget en prenant en compte le nouvel état du widget. */
	protected _refresh() {}

	/**
	 * Initialisation de l'état et configuration du widget.
	 * Méthode appelée une seule fois dans la vie du widget.
	 */
	protected _initialize(init: any): void {}

	/**
	 * Création ou enrichissement d'un objet d'init à partir des attributs de l'élement destiné à
	 * alimenter par défaut _initialize(init). Si l'implémentation utilise l'attribut
	 * pour mémoriser le state ou la config correspondant, la propriété n'est pas renseignée
	 * dans l'objet init retourné.
	 *
	 * @param init Objet déjà construit, éventuellement pré-renseigné, avec un reg par exemple.
	 */
	buildInitFromAtts(init?: OSkinableInit): any {
		if (!init) init = {};
		if (this.hasAttribute('skin')) init.skin = BASIS.extractAttr(this, 'skin');
		if (this.hasAttribute('skinOver')) init.skinOver = BASIS.extractAttr(this, 'skinOver');
		return init;
	}

	/** Installe le skin du widget, éventuellement personnalisé par la config. */
	_initAndInstallSkin(defaultSkin: string | null, init: OSkinableInit | null) {
		const skin = init?.skin || defaultSkin;
		const skinOver = init?.skinOver;
		if (skin || skinOver) {
			const reg = this.findReg(init);
			if (skin) reg.installSkin(skin, this.shadowRoot);
			if (skinOver) {
				for (const sk of skinOver.split(' ')) reg.installSkin(sk, this.shadowRoot);
			}
		}
	}

	/** Méthode utilitaire pour attacher un shadow et définir le skin et des enfants dessus */
	_attach(defaultSkin: string | null, init: OSkinableInit | null, ...children: Node[]): ShadowRoot {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(defaultSkin, init);
		DOM.append(sr, ...children);
		return sr;
	}

	findReg<ENV>(init: IRegPointer<ENV>): IReg<ENV> {
		return REG.findReg(this, init);
	}

	_JsxProps: any;
}

/**
 * Base pour tous les éléments IEltInitableAsync et IEltStatable.
 */
export class BaseElementAsync extends BaseElement implements IEltInitableAsync {

	initStatus: 'unInited' | 'pending' | 'inited' | 'initFailed' = 'unInited';

	initializedAsync = new Promise<this>((resolve, reject) => {
		this._initResolved = resolve;
		this._initRejected = reject;
	});
	protected _initResolved: (value: this) => void;
	protected _initRejected: (reason?: any) => void;

	get initialized(): boolean {return this.initStatus === 'inited'}

	initialize(init: any = this.buildInitFromAtts()): this {
		if (this.initStatus === 'unInited') {
			this.initStatus = 'pending';
			this._initialize(init).then(() => {
				this.initStatus = 'inited';
				this._refreshFreeze--;
				const cb = this._initResolved;
				this._initResolved = null;
				this._initRejected = null;
				cb(this);
			}, (e) => {
				this.initStatus = 'initFailed';
				const cb = this._initRejected;
				this._initResolved = null;
				this._initRejected = null;
				cb(e);
				throw e;
			});
		}
		return this;
	}

	connectedCallback() {
		this.initialize();
		this.initializedAsync.then(() => {
			if (this._refreshFreeze === 0) this._refresh();
		});
	}

	/** A appeler si le widget est réinitialisé. */
	reinitializeStarted(promise: Promise<void>) {
		this.initStatus = 'pending';
		this.initializedAsync = promise.then(
			() => {
				this.initStatus = 'inited';
				return this
			},
			(e) => {
				this.initStatus = 'initFailed';
				throw e;
			}
		);
	}

	protected _initialize(init: any): Promise<void> {throw Error("not implemented")};
}

export function isEltInitableAsync(elt: any): elt is IEltInitableAsync {return elt && elt.initializedAsync}

export function isEltInitableAsyncPending(elt: any): elt is IEltInitableAsync {return elt && elt.initializedAsync && (elt.initStatus === 'unInited' || elt.initStatus === 'pending')}

type Writable<T> = { -readonly [P in keyof T]: T[P] };
export type PickInit<T, K extends keyof T> = Partial<Writable<Pick<T, K>>>;

REG.reg.registerSkin('basis', 1, /* language=CSS */ `
	:root {
		font-family: var(--font-default);
		font-size: 10pt;
		word-break: break-word;
		background-color: var(--bgcolor);
		color: var(--color);
		accent-color: var(--selection-bgcolor);
	}
	
	:root::selection {
		color: var(--selection-color);
		background: var(--selection-bgcolor);
	}
`);

/**
 * Composant générique qui crée un shadowRoot et initilise un skin et un skinOver si spécifiés.
 */
export interface Box extends BaseElement {
	initialize(init?: OSkinableInit): this;
}

export class Box extends BaseElement {

	protected _initialize(init: OSkinableInit) {
		if (!this.shadowRoot) this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
	}
}

customElements.define('c-box', Box);


/**
 * Widget affichant un label et/ou une icone.
 *
 * Attributes : cf BaseElement +
 * State live attributes:      hidden
 * State init attributes:      label, icon
 * Classes:                    vertical
 */

export interface OLabelInit extends OSkinableInit {
	label?: string
	icon?: string
	title?: string
	hidden?: boolean
	/**
	 * Inclut l'icone et le span du label en fils direct (pas en shadowDom).
	 * Facilite le stylage externe mais désactive tout stylage standard.
	 * Les propriétés skin et skinOver ne sont alors pas utilisées.
	 */
	noShadow?: boolean;

	/**
	 * Ajoute les propriétés pour gérer l'ellipse du contenu. Si noShadow est à true,
	 * c'est au parent de les ajouter.
	 * Si aucun title n'est défini dans l'init, un title basé sur le contenu textuel
	 * du label sera ajouté / enlever en fonction de la présence de l'ellipse.
	 */
	ellipsis?: boolean;
}

export interface Label extends BaseElement {
	initialize(init?: OLabelInit): this;
}

export class Label extends BaseElement {
	protected _label: string;
	protected _icon: string;

	get icon(): string {return this._icon}

	set icon(value: string) {
		if (this._icon != value) {
			this._icon = value;
			this.refresh();
		}
	}

	get label(): string {return this._label}

	set label(val: string) {
		if (this._label != val) {
			this._label = val;
			this.refresh();
		}
	}

	get hidden() {return this.hasAttribute('hidden')}

	set hidden(val: boolean) {
		if (DOM.setHidden(this, val)) this.refresh();
	}

	_iconElt: HTMLElement;
	_labelElt: HTMLElement;

	protected _initialize(init: OLabelInit) {
		if ('icon' in init) this._icon = init.icon;
		if ('label' in init) this._label = init.label;
		if ('title' in init) this.title = init.title;
		if ('hidden' in init) this.hidden = init.hidden;
		if ('ellipsis' in init) DOM.setAttrBool(this, 'ellipsis', init.ellipsis);
		if (!init.noShadow) {
			const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
			this._initSkin(init);
			sr.appendChild(document.createElement('slot'));
		}
	}

	buildInitFromAtts(init?: OLabelInit): OLabelInit {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute('label')) init.label = BASIS.extractAttr(this, 'label');
		if (this.hasAttribute('icon')) init.icon = BASIS.extractAttr(this, 'icon');
		return init;
	}

	protected _initSkin(init: OSkinableInit) {
		this._initAndInstallSkin(this.localName, init);
	}

	protected _refresh() {
		if (this.hidden) return;

		const icon = this.icon;
		if (icon) {
			if (!this._iconElt) this._createIcon();
			if (this._lastUrlIcon !== icon) {
				//NOTE : DOM.setStyle() avec une url ne marche pas à cause des escapings.
				this._lastUrlIcon = icon;
				this.style.setProperty('--icon-img', icon.startsWith('--') ? `var(${icon})` : `url("${icon}")`);
			}
			DOM.setHidden(this._iconElt, false);
		} else {
			if (this._iconElt) DOM.setHidden(this._iconElt, true);
		}

		const label = this.label;
		if (label != null) {
			if (!this._labelElt) this._createLabel();
			DOM.setTextContent(this._labelElt, label);
			DOM.setHidden(this._labelElt, false);
		} else {
			if (this._labelElt) DOM.setHidden(this._labelElt, true);
		}
	}

	protected _lastUrlIcon: string;

	/** crétaion de l'icon à la demande. */
	_createIcon() {
		this._iconElt = (this.shadowRoot || this).insertBefore(<span class='icon'/>, this._labelElt);
	}

	/** crétaion du label à la demande. */
	_createLabel() {
		this._labelElt = (this.shadowRoot || this).insertBefore(<span class='label'/>, this._iconElt ? this._iconElt.nextSibling : null);
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.hasAttribute('ellipsis') && !this.title) BASIS.titleOnEllipsisObserver.observe(this);
	}

	disconnectedCallback() {
		if (this.hasAttribute('ellipsis')) BASIS.titleOnEllipsisObserver.unobserve(this);
	}
}

customElements.define('c-label', Label);

REG.reg.registerSkin('c-label', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		justify-content: center;
	}

	:host([ellipsis]) {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.icon {
		height: var(--icon-size);
		width: var(--icon-size);
		min-height: var(--icon-size);
		min-width: var(--icon-size);
		background: var(--icon-color) var(--icon-img) no-repeat center / contain;
		filter: var(--filter);
		/*mask: var(--icon-img) no-repeat center / contain;*/
		/*-webkit-mask: var(--icon-img) no-repeat center / contain;*/
		/*background-blend-mode: multiply;*/
	}

	.icon:not([hidden]) + .label {
		-webkit-margin-start: 0.3em;
		margin-inline-start: 0.3em;
	}

	:host(.vertical) {
		flex-direction: column;
		align-items: center;
	}

	:host(.vertical) > .icon {
		height: calc(var(--icon-size) * 1.5);
		width: calc(var(--icon-size) * 1.5);
	}
`);

/**
 * Widget c-filesystem-path :affichage d'un path FS, avec btns d'action (ouverture, révéler)
 *
 * Attributs : cf Label +
 * - type : 'dir' par défaut
 * - openBtn : false par défaut. SSi type='file'. Visible QUE dans le contexte "electron"
 * - revealBtn : true par défaut. Visible QUE dans le contexte "electron"
 *
 * Class :
 * - small
 */

export interface OFileSystemPathInit extends OLabelInit {
	openBtn?: boolean
	revealBtn?: boolean
	type?: 'file' | 'dir'
}

export interface FileSystemPath extends Label {
	initialize(init?: OFileSystemPathInit): this;
}

export class FileSystemPath extends Label {

	protected _openBtnElt: Button;

	protected _revealBtnElt: Button;

	_initialize(init: OFileSystemPathInit) {
		super._initialize(init);
		const reg = this.findReg(init);
		this.openBtn = ("openBtn" in init) ? init.openBtn : false;
		this.revealBtn = ("revealBtn" in init) ? init.revealBtn : true;
		this.type = init.type || 'dir';
		this.addEventListener("click", (event) => {
			if (this._labelElt)
				window.getSelection().selectAllChildren(this._labelElt);
		})
	}

	protected _initSkin(init: OSkinableInit) {
		const reg = this.findReg(init);
		reg.installSkin('c-label', this.shadowRoot);
		super._initSkin(init);
	}

	/** utile pour titleOnEllipsisObserver */
	get textContent(): string {
		return this._labelElt.textContent;
	}

	get openBtn(): boolean {return this.hasAttribute("openBtn")}

	set openBtn(md: boolean) {
		DOM.setAttrBool(this, "openBtn", md)
	}

	get revealBtn(): boolean {return this.hasAttribute("revealBtn")}

	set revealBtn(md: boolean) {
		DOM.setAttrBool(this, "revealBtn", md)
	}

	get type(): 'file' | 'dir' {return this.getAttribute("type") as any}

	set type(lv: 'file' | 'dir') {
		if (lv)
			this.setAttribute("type", lv);
		else
			this.removeAttribute("type");
	}

	buildInitFromAtts(init?: OFileSystemPathInit): OMsgLabelInit {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute('openBtn')) init.openBtn = true;
		if (this.hasAttribute('revealBtn')) init.revealBtn = true;
		if (this.hasAttribute('type')) init.type = BASIS.extractAttr(this, 'type') as any;
		return init;
	}

	protected _refresh() {
		super._refresh();
		const parentElt = this.shadowRoot || this;
		if (Desk.electron && this.openBtn && !this._openBtnElt)
			this._openBtnElt = parentElt.insertBefore(<c-button class="openBtn" î={{title: "Ouvrir...", icon: "/@skin@/commons/icons/open.svg", role: "spinbutton"} as OButtonActionsInit<any>}
																													onclick={this.doOpen}/>, DOM.findLastChild(parentElt)) as Button;
		if (Desk.electron && this.revealBtn && !this._revealBtnElt)
			this._revealBtnElt = parentElt.insertBefore(<c-button class="revealBtn" î={{title: "Révéler...", icon: "/@skin@/commons/icons/reveal.svg", role: "spinbutton"} as OButtonActionsInit<any>}
																														onclick={this.doReveal}/>, DOM.findLastChild(parentElt)) as Button;
	}

	/** Révéler le path */
	protected doReveal(ev: Event): void {
		window.postMessage({type: "client:modules:localServer:reveal", path: DOMSH.findHost<FileSystemPath>(this).textContent}, location.origin)
		ev.stopPropagation();
	}

	/** Ouvrir le path */
	protected doOpen(ev: Event): void {
		window.postMessage({type: "client:modules:localServer:open", path: DOMSH.findHost<FileSystemPath>(this).textContent}, location.origin);
		ev.stopPropagation();
	}

	/**
	 * Si msg==null, c-filesystem-path passé en display:none.
	 * Si msg==="", c-filesystem-path vide mais pas hidden
	 */
	setPath(msg: string | null, type: 'file' | 'dir' | null, openBtn?: boolean | null, revealBtn?: boolean | null, icon?: string): this {
		if (msg == null) {
			this.hidden = true;
		} else {
			try {
				this.refreshFreeze(1);
				this.label = msg;
				if (icon !== null) this.icon = icon;
				if (type !== null) this.type = type;
				if (openBtn !== null) this.openBtn = openBtn;
				if (revealBtn !== null) this.revealBtn = revealBtn;
				if (this._openBtnElt)
					this._openBtnElt.disabled = msg === "";
				if (this._revealBtnElt)
					this._openBtnElt.disabled = msg === "";
				this.hidden = false;
			} finally {
				this.refreshFreeze(-1);
				this.refresh();
			}
		}
		return this;
	}

}

REG.reg.registerSkin('c-filesystem-path', 1, /* language=CSS */ `
	:host {
		justify-content: left;
		align-items: center;
	}

	:host([hidden]) {
		display: none;
	}

	:host(:not([type=file])) .openBtn {
		display: none;
	}

	:host(.small) {
		font-size: .8em;
		color: var(--info-color);
	}

	.openBtn, .revealBtn {
		display: inline-flex;
		unicode-bidi: isolate;
	}

`);

customElements.define('c-filesystem-path', FileSystemPath);


/**
 * Widget c-msg
 *
 * Attributs : cf Label +
 * - level
 * - standard-msg
 *
 * Classes :
 * - headband : affichage sous forme de bandeau.
 */

export interface OMsgLabelInit extends OLabelInit {
	level?: 'info' | 'error' | 'warning' | 'valid' | null
	standardMsg?: 'loading' | 'saving' | 'deleting' | 'needAuth' | 'accessDenied' | 'netError' | null
}

export interface MsgLabel extends Label {
	initialize(init?: OMsgLabelInit): this;
}

export class MsgLabel extends Label {

	_initialize(init: OMsgLabelInit) {
		if (init.level) this.level = init.level;
		if (init.standardMsg) this.setStandardMsg(init.standardMsg);
		super._initialize(init);
	}

	buildInitFromAtts(init?: OMsgLabelInit): OMsgLabelInit {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute('standard-msg')) init.standardMsg = BASIS.extractAttr(this, 'standard-msg') as any;
		return init;
	}

	get level(): 'info' | 'error' | 'warning' | 'valid' | null {return this.getAttribute("level") as any}

	set level(lv: 'info' | 'error' | 'warning' | 'valid' | null) {
		if (lv)
			this.setAttribute("level", lv);
		else
			this.removeAttribute("level");
	}

	setStandardMsg(msg: 'loading' | 'saving' | 'deleting' | 'needAuth' | 'accessDenied' | 'netError' | null): this {
		if (!msg) {
			this.hidden = true;
		} else {
			this.hidden = false;
			this.className = msg;
			switch (msg) {
			case 'loading':
				DOM.setAttr(this, "level", "info");
				this.label = "Chargement en cours...";
				break;
			case 'saving':
				DOM.setAttr(this, "level", "info");
				this.label = "Enregistrement en cours...";
				break;
			case 'deleting':
				DOM.setAttr(this, "level", "info");
				this.label = "Suppression en cours...";
				break;
			case 'needAuth':
				DOM.setAttr(this, "level", "info");
				this.label = "Connexion nécessaire";
				break;
			case 'accessDenied':
				DOM.setAttr(this, "level", "error");
				this.label = "Autorisation refusée";
				break;
			case 'netError':
				DOM.setAttr(this, "level", "error");
				this.label = "Accès au serveur impossible";
				break;
			}
		}
		return this;
	}

	/**
	 * Si msg==null, c-msg passé en display:none.
	 * Si msg==="", c-msg vide mais pas hidden
	 */
	setCustomMsg(msg: string | null, level?: 'info' | 'error' | 'warning' | 'valid' | null, icon?: string): this {
		if (msg == null) {
			this.hidden = true;
		} else {
			try {
				this.refreshFreeze(1);
				this.label = msg;
				this.icon = icon;
				this.level = level;
				this.hidden = false;
			} finally {
				this.refreshFreeze(-1);
				this.refresh();
			}
		}
		return this;
	}
}

REG.reg.registerSkin('c-msg', 1, /* language=CSS */ `
	:host {
		flex: 1 0 auto;
		display: flex;
		min-height: 0;
		min-width: 0;
		justify-content: center;
		align-items: center;
	}

	:host([hidden]) {
		display: none;
	}

	:host(.headband) {
		flex: 0 0 auto;
		min-height: 1.7em;
	}

	:host([level=info]) {
		color: var(--info-color);
		font-style: italic;
	}

	:host([level=error]) {
		color: var(--error-color);
	}

	:host([level=warning]) {
		color: var(--warning-color);
	}

	:host([level=valid]) {
		color: var(--valid-color);
	}
`);

customElements.define('c-msg', MsgLabel);

/**
 * Message qui se place par dessus d'un objet de référence dans le but d'en bloquer les intercations
 * et d'informer l'utilisateur via le message.
 */
export class MsgOver extends MsgLabel {
	over: HTMLElement;
	protected _wasStatic: boolean;

	protected _initSkin(init: OSkinableInit) {
		const reg = this.findReg(init);
		reg.installSkin('c-msg', this.shadowRoot);
		this._initAndInstallSkin(this.localName, init);
	}

	showMsgOver(node: HTMLElement): this {
		while (node && node instanceof HTMLIFrameElement || node instanceof HTMLImageElement || node instanceof HTMLMediaElement) {
			//Impossible d'accrocher un msgOver sur ces éléments
			//XXX A affiner... quid si display = 'contents', 'table'...
			node = DOMSH.getFlatParentElt(node) as HTMLElement;
		}
		if (this.over && this.over !== node) this.removeMsg();
		this.over = node;
		const st = window.getComputedStyle(node);
		if (st.position === "static") {
			node.style.position = "relative";
			this._wasStatic = true;
		} else {
			this._wasStatic = false;
		}
		(node.shadowRoot || node).appendChild(this);
		return this;
	}

	async waitFor<T>(cb: PromiseLike<T> | (() => PromiseLike<T>)): Promise<T> {
		try {
			return await (typeof cb === 'function' ? (cb as () => PromiseLike<any>)() : cb);
		} finally {
			this.removeMsg();
		}
	}

	removeMsg(): this {
		if (!this.over) return;
		if (this._wasStatic) this.over.style.position = "static";
		if (this.parentNode) this.remove();
		return this;
	}
}

REG.reg.registerSkin('c-msg-over', 1, /* language=CSS */ `
	:host {
		position: absolute;
		left: 0;
		top: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, .5);
		z-index: 1000000;
	}

	.label {
		background: linear-gradient(to top, transparent, var(--bgcolor) .5em, var(--bgcolor) 50%, transparent),
		linear-gradient(to bottom, transparent, var(--bgcolor) .5em, var(--bgcolor) 50%, transparent);
		border-radius: .5em;
		padding: .5em;
	}
`);

customElements.define('c-msg-over', MsgOver);


/**
 * Widget de base: focusable et clickable affichant un label et/ou une icone.
 * Usages: bouton, checkbox, radio...
 *
 * Attributes : cf Label +
 * State live attributes: disabled
 * State init attributes: tabindex-custom
 */
export interface OClickableInit extends OLabelInit {
	disabled?: boolean
	tabIndexCustom?: number
}

export interface Clickable extends Label {
	initialize(init?: OClickableInit, ...args: any[]): this;
}

/** Widgets susceptibles de déclencher une action comme un bouton, un checkbox, un radiobutton... */
export abstract class Clickable extends Label {
	_tabIndexCustom: number;

	get disabled(): boolean {return this.hasAttribute('disabled')}

	set disabled(val: boolean) {
		if (DOM.setAttrBool(this, 'disabled', val)) this.refresh();
	}

	/**
	 * 0 par défaut.
	 * -1 pour empêcher que ce 'clickable', même enabled, soit accessible par navigation (tab).
	 * -2 pour empêcher que ce 'clickable', même enabled, soit focusable (revient à suppr l'attribut tabindex)
	 */
	get tabIndexCustom(): number {return this._tabIndexCustom}

	set tabIndexCustom(val: number) {
		if (this._tabIndexCustom !== val) {
			this._tabIndexCustom = val;
			this.refresh();
		}
	}

	protected _initialize(init: OClickableInit) {
		BASIS.makeClickable(this);
		if (init.disabled) DOM.setAttrBool(this, 'disabled', true);
		this._tabIndexCustom = init.tabIndexCustom || 0;
		super._initialize(init);
	}

	buildInitFromAtts(init?: OClickableInit): OClickableInit {
		init = super.buildInitFromAtts(init);
		init.tabIndexCustom = '_tabIndexCustom' in this ? this._tabIndexCustom : BASIS.extractAttrAsNumber(this, 'tabindex-custom', 0);
		return init;
	}

	protected _refresh() {
		super._refresh();
		this.tabIndex = this.disabled ? -1 : this.tabIndexCustom;
	}

}

/** Skin pour des petits scrollbar. */
REG.reg.registerSkin("scroll/small", 1,  /* language=CSS */ `
	::-webkit-scrollbar {
		width: .6em;
		height: .6em;
	}

	::-webkit-scrollbar-track {
		background-color: var(--scroll-color);
		filter: opacity(.1);
	}

	::-webkit-scrollbar-thumb {
		border-radius: .2em;
		box-shadow: inset 0 0 .3em var(--scroll-thumb-color);
	}

	* {
		scrollbar-width: thin;
	}
`);


/** Skin pour des larges scrollbar. */
REG.reg.registerSkin("scroll/large", 1,  /* language=CSS */ `
	::-webkit-scrollbar {
		width: 1em;
		height: 1em;
		background: var(--scroll-color);
	}

	::-webkit-scrollbar-thumb {
		border-radius: .2em;
		box-shadow: inset 0 0 .3em var(--scroll-thumb-color);
	}

	::-webkit-scrollbar-track {
		background: var(--scroll-color, #f3f3f3);
	}

	::-webkit-scrollbar-button:single-button {
		background-color: #bbbbbb;
		display: block;
		box-shadow: inset 0 0 .2em var(--scroll-thumb-color);
		height: 1em;
		width: 1em;
	}

	::-webkit-scrollbar-corner {
		background-color: transparent;
	}

	::-webkit-scrollbar-button:single-button:hover {
		box-shadow: inset 0 0 .4em var(--scroll-thumb-color);
	}

	::-webkit-scrollbar-button:single-button:vertical:decrement {
		background: center center no-repeat url(/@skin@/commons/widgets/scrollUp.svg);
		filter: var(--filter);
	}

	::-webkit-scrollbar-button:single-button:vertical:increment {
		background: center center no-repeat url(/@skin@/commons/widgets/scrollDown.svg);
		filter: var(--filter);
	}

	::-webkit-scrollbar-button:single-button:horizontal:decrement {
		background: center center no-repeat url(/@skin@/commons/widgets/scrollLeft.svg);
	}

	::-webkit-scrollbar-button:single-button:horizontal:increment {
		background: center center no-repeat url(/@skin@/commons/widgets/scrollRight.svg);
	}
`);


/** Tests de compat des navigateurs pour le code de Wui_Commons.*/
Desk.compatTable.set('commons', function (): boolean {
	if (!window.customElements || !HTMLElement.prototype.attachShadow) return false;
	if (!window.requestIdleCallback) return false;
	// On ne considère pas ResizeObserver obligatoire dans commons bien qu'utilisé sur les popups (mais sur des cas relativement marginaux). Cela permet l'usage de Firefox 68 ESR.
	//ajouter MutationObserver, IntersectionObserver ?
	return true;
});


/**
 * Custom élément construit dynamiquement.
 * Indispensable quand des modules doivent pouvoir être chargés dans le même contexte de window
 * mais peuvent être en conflit (différentes versions d'un pack, etc.)
 */
export interface FreeElement extends BaseElement {
	initialize(init: OFreeElementInit): this
}

export interface OFreeElementInit {
	freeInitialize: (this: FreeElement, init: OFreeElementInit) => void
}

export class FreeElement extends BaseElement {

	protected _initialize(init: OFreeElementInit) {
		init.freeInitialize.call(this, init);
	}

	buildInitFromAtts(init?: OSkinableInit): any {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute('svc')) {
			const reg = REG.findReg(this);
			const svc = reg.getSvc(this.getAttribute('svc')) as OFreeElementInit;
			if (svc) init = Object.assign(init, svc)
		}
		return init;
	}
}

customElements.define('c-free', FreeElement);


export interface FreeElementAsync extends BaseElementAsync {
	initialize(init: OFreeElementAsyncInit): this
}

export interface OFreeElementAsyncInit {
	freeInitialize: (this: FreeElement, init: OFreeElementAsyncInit) => Promise<void>
}

export class FreeElementAsync extends BaseElementAsync {

	protected async _initialize(init: OFreeElementAsyncInit) {
		return init.freeInitialize.call(this, init);
	}

	buildInitFromAtts(init?: OSkinableInit): any {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute('svc')) {
			const reg = REG.findReg(this);
			const svc = reg.getSvc(this.getAttribute('svc')) as OFreeElementAsyncInit;
			if (svc) init = Object.assign(init, svc)
		}
		return init;
	}

}

customElements.define('c-free-async', FreeElementAsync);


export namespace BASIS {

	/** Rend un élément focusable et exécutable. */
	export function makeClickable(elt: HTMLElement) {
		elt.tabIndex = 0;
		elt.addEventListener('keypress', function (ev: KeyboardEvent) {
			if (LANG.in(ev.key, 'Enter', ' ') && !ACTION.isAnyControlPressed(ev)) {
				ev.stopImmediatePropagation();
				ev.preventDefault();
				this.click();
			}
		});
	}

	export function extractAttr(node: Element, name: string): string {
		return node.getAttribute(name);
		//let val = node.getAttribute(name);
		//if (val != null) node.removeAttribute(name);
		//return val;
	}

	export function extractAttrAsNumber(node: Element, name: string, defaultVal: number): number {
		if (node.hasAttribute(name)) return Number.parseInt(extractAttr(node, name)) || defaultVal;
		return defaultVal;
	}

	export function refreshTree(from: Element | Document | DocumentFragment, excludeFrom?: boolean) {
		const tw = document.createTreeWalker(from, NodeFilter.SHOW_ELEMENT);
		if (excludeFrom && !tw.nextNode()) return;
		do {
			if (isEltRefreshable(tw.currentNode)) tw.currentNode.refresh();
		} while (tw.nextNode());
	}

	/**
	 * Efface tous les fils sauf les balises <style>.
	 * @see VIEWS.clearContent()
	 */
	export function clearContent(sr: ShadowRoot) {
		let ch = sr.lastChild;
		while (ch) {
			if (ch.localName !== "style") {
				const prev = ch.previousSibling as ChildNode;
				sr.removeChild(ch);
				ch = prev;
			} else {
				ch = ch.previousSibling as ChildNode;
			}
		}
	}

	/**
	 * Instancie un nouvel objet init en s'appuyant sur un éventuel objet existant en prototype
	 * et en associant un registre.
	 */
	export function newInit<I extends IRegPointer<any>>(from: I, reg: IReg<any>): I {
		const i = Object.create(from || null);
		i.reg = reg;
		return i;
	}

	/**
	 * ResizeObserver s'occupant de définir un title basé sur le contenu en fonction
	 * de l'état d'overflow, utile quand un ellipsis est défini sur l'élement.
	 */
	export const titleOnEllipsisObserver = new ResizeObserver((entries) => {
		for (const entry of entries) {
			const target = entry.target as HTMLElement;
			if (target.scrollWidth > Math.ceil(entry.contentRect.width) || target.scrollHeight > Math.ceil(entry.contentRect.height) /* check height aussi pour ellipsis avec css line-clamp */) {
				target.title = entry.target.textContent;
			} else {
				target.title = "";
			}
		}
	});

	/**
	 * Rend visible un elt dans un scrollable container en gérant le scroll smooth si "raisonnable"
	 * (contournement imparfait de l'instabilité de chromium sur le scroll smooth).
	 */
	export function ensureVisible(elt: HTMLElement, scrollCtn: HTMLElement | null, blockPos: 'nearest' | 'start' = 'start') {
		if (scrollCtn) {
			if (getComputedStyle(scrollCtn).scrollBehavior === 'smooth') {
				const target = elt.getBoundingClientRect();
				const scrollBox = scrollCtn.getBoundingClientRect();
				let delta: number;
				if (blockPos === 'nearest') {
					delta = Math.min(Math.abs(target.bottom - scrollBox.top), Math.abs(target.top - scrollBox.bottom));
				} else {
					delta = Math.abs(target.top - scrollBox.top);
				}
				if (delta > scrollBox.height * 2) {
					//console.log("box scrollBehavior:::instant");
					scrollCtn.style.scrollBehavior = 'auto';
					elt.scrollIntoView({block: blockPos, behavior: 'auto'});
					scrollCtn.style.scrollBehavior = 'smooth';
				} else {
					//console.log("box scrollBehavior:::smooth");
					elt.scrollIntoView({block: blockPos, behavior: 'smooth'});
				}
			} else {
				//smooth désactivé.
				elt.scrollIntoView({block: blockPos});
			}
		} else {
			elt.scrollIntoView({block: blockPos, behavior: 'smooth'});
		}
	}

	export const DEBUG = false;

}
